import { FeatureFlag } from '@ps/web-core-enums';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import useFromEvent from '@swf/core/hooks/useFromEvent';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import { select } from '@swf/core/store';
import type { RmfTriggerPoint } from '@swf/core/types/Window';

import useRmfServices from '../components/RMF/hooks/useRmfServices';
import { getRmfRequiredPath } from '../utils/cashier';

type WizardJump = (target: string, triggerPoint: RmfTriggerPoint) => void;

const useTriggerRMFFlow = (): WizardJump => {
  const { jumpForward, jumpBack } = useWizardJump();
  const { rmfUpdateFormData } = useRmfServices();
  const { rmfRealMoneyInformationCheck } = useRmfServices();
  const { data } = useWizardContext();
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const site = useSelector(select.cms.getSiteId);
  const isSignUpOnePageEnabled = useFeature(FeatureFlag.SwcSignupOnePage);
  const swcRmfUKPhoneAfterAddress = useFeature(FeatureFlag.SwcRmfUKPhoneAfterAddress);
  const swcRmfUKPhoneAfterName = useFeature(FeatureFlag.SwcRmfUKPhoneAfterName);
  const isUKDefaultFlow = !swcRmfUKPhoneAfterAddress && !swcRmfUKPhoneAfterName;
  const path = getRmfRequiredPath(site, isSignUpOnePageEnabled, isUKDefaultFlow);
  const fromEvent = useFromEvent();
  const wizardContext = useWizardContext();
  const exitWizard = useWizardExit();
  const swcR2DWebFeatures = useFeature(FeatureFlag.SwcR2DWebFeatures);

  const handleMessage = useCallback(
    ({ detail }: CustomEvent) => {
      const { message } = detail.payload;

      if (data.rmfTargetToJump && message === 'rmfCompleted') {
        jumpForward(data.rmfTargetToJump);
      }
    },
    [data.rmfTargetToJump, jumpForward]
  );

  const handleClose = useCallback(async () => {
    const { realMoneyInformation } = await rmfRealMoneyInformationCheck();
    if (!realMoneyInformation && data.rmfTargetToJump) {
      return jumpForward(data.rmfTargetToJump);
    } else {
      exitWizard();
    }
  }, [data.rmfTargetToJump, exitWizard, jumpForward, rmfRealMoneyInformationCheck]);

  useEffect(() => {
    if (isMobileEmbedded && !swcR2DWebFeatures) {
      fromEvent(document, 'starsbroadcast').subscribe((e) => handleMessage(e as CustomEvent));
      fromEvent(document, 'starsresume').subscribe(() => handleClose());
    }
  }, [fromEvent, handleMessage, isMobileEmbedded, jumpForward, handleClose, swcR2DWebFeatures]);

  return useCallback(
    async (target: string, triggerPoint: RmfTriggerPoint) => {
      data.rmfTargetToJump = target;
      await rmfUpdateFormData(wizardContext);
      if (isMobileEmbedded && !swcR2DWebFeatures) {
        window.starsweb?.app.openRmf?.(triggerPoint);
        return;
      } else {
        if (window.location.pathname === '/signup') {
          return jumpBack(path);
        } else {
          return jumpForward(path);
        }
      }
    },
    [
      data,
      isMobileEmbedded,
      jumpBack,
      jumpForward,
      path,
      rmfUpdateFormData,
      swcR2DWebFeatures,
      wizardContext
    ]
  );
};

export default useTriggerRMFFlow;
